/**
 * Per-path dirty detection for the Voluum Standard tab draft.
 *
 * Compares a single path between the live Voluum payload and the in-flight
 * draft so the UI can highlight rows the user has personally edited (yellow
 * ring + restore button).
 *
 * Comparison is intentionally narrow: only the fields the row UI exposes
 * (active, weight, name). Everything else is owned by Voluum and not relevant
 * for the "user changed this" indicator.
 */

import type {
  VoluumFlowResource,
  VoluumFlowUpdate,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

export type PathDirtyField = 'active' | 'weight' | 'name';

export interface PathDirtyResult {
  dirty: boolean;
  fields: ReadonlySet<PathDirtyField>;
}

function findPath(
  flow: VoluumFlowResource | VoluumFlowUpdate | undefined,
  pathId: string,
): VoluumPathUpdate | undefined {
  if (!flow) return undefined;
  const fromDefaults = flow.defaultPaths?.find((p) => p.id === pathId);
  if (fromDefaults) return fromDefaults;
  for (const group of flow.conditionalPathsGroups ?? []) {
    const found = group.paths?.find((p) => p.id === pathId);
    if (found) return found;
  }
  return undefined;
}

export function getPathDirtyFields(
  live: VoluumFlowResource | undefined,
  draft: VoluumFlowUpdate | null,
  pathId: string | undefined,
): PathDirtyResult {
  if (!pathId || !draft || !live) {
    return { dirty: false, fields: new Set() };
  }
  const livePath = findPath(live, pathId);
  const draftPath = findPath(draft, pathId);
  if (!livePath || !draftPath) {
    return { dirty: Boolean(draftPath) !== Boolean(livePath), fields: new Set() };
  }
  const fields = new Set<PathDirtyField>();
  if (livePath.active !== draftPath.active) fields.add('active');
  if (livePath.weight !== draftPath.weight) fields.add('weight');
  if ((livePath.name ?? '') !== (draftPath.name ?? '')) fields.add('name');
  return { dirty: fields.size > 0, fields };
}

export function restorePath(
  draft: VoluumFlowUpdate,
  live: VoluumFlowResource,
  pathId: string,
): void {
  const livePath = findPath(live, pathId);
  const draftPath = findPath(draft, pathId);
  if (!livePath || !draftPath) return;
  draftPath.active = livePath.active;
  draftPath.weight = livePath.weight;
  draftPath.name = livePath.name;
}
